import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useJobsStatus } from '../../api/jobs'
import { useLiveSubscription, useScopeLive } from '../../contexts/LiveEventsContext'
import { Alert, AlertDescription } from '../ui/alert'

/**
 * ProcessingBlockersBanner is the compact, non-blocking notification shown on
 * the jobs and documents pages whenever `blocking_count > 0` (spec
 * 2026-08-18-01 §4).
 *
 * Deliberately carries no per-blocker detail — that is the blockers page's
 * job — and renders nothing for a backlog that is merely draining (pending
 * jobs, nothing blocking).
 */
export function ProcessingBlockersBanner({ org }: { org: string }) {
    const { t } = useTranslation('jobs')
    useLiveSubscription({ entity: 'jobs' })
    const isJobsScopeLive = useScopeLive({ entity: 'jobs' })
    const { data } = useJobsStatus(org, isJobsScopeLive)

    const blocking = data?.blocking_count ?? 0
    if (blocking === 0) return null

    return (
        <Alert className="mb-4 border-destructive/40 bg-destructive/5" data-testid="processing-blockers-banner">
            <AlertDescription className="flex flex-wrap items-center gap-2">
                <span aria-hidden="true">⛔</span>
                <span>{t('blockers.banner', { count: blocking })}</span>
                <Link
                    to="/$org/jobs/blockers"
                    params={{ org }}
                    className="underline font-medium"
                    data-testid="processing-blockers-banner-link"
                >
                    {t('blockers.banner_link')}
                </Link>
            </AlertDescription>
        </Alert>
    )
}
